/**
 * Client-side analytics: PostHog init, React hooks, and dev override tooling.
 *
 * Why custom hooks instead of posthog-js/react?
 *
 * The PostHog React SDK (useFeatureFlagVariantKey, etc.) subscribes to
 * onFeatureFlags and returns `string | boolean | undefined`. We need three
 * things it doesn't provide:
 *
 * 1. **isLoading** — PostHog React hooks don't expose the `featureFlagsReloading`
 *    event, so there's no way to show a loading state during logout→login flag
 *    refetches.
 * 2. **isOverridden** — PostHog React hooks don't surface whether a flag is
 *    currently overridden via `$override_feature_flags`.
 * 3. **Typed variants** — PostHog hooks take raw string keys and return untyped
 *    values. Our hooks accept typed flag names (`'backendVersion'`) and return
 *    typed variants (`'python' | 'effect-ts'`), never `undefined`.
 *
 * The underlying reactivity pattern is identical (getFeatureFlag + onFeatureFlags
 * subscription), so wrapping their hooks would add PostHogProvider to the tree
 * without saving any code.
 */
import { posthog } from 'posthog-js';
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  decodePricingEdgeCode,
  PRICING_TEST_V2_FLAG_KEY,
} from '@listening/api-contract/pricing-experiment';
import { isBotUserAgent } from './bucketing/bot-ua';
import { ClientFlags, type ClientFlagName } from './flags/client-flags';
import type { FlagVariant, FeatureFlagDef } from './flags/flag-types';
import {
  exposureLabelForFlagKey,
  exposureTriggerForFlagKey,
  type PolicyExposureLabel,
} from './flags/exposure-policy';
import type { ClientEvents } from './events/client-events';

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

type FlagRecord = Record<string, FeatureFlagDef>;

declare global {
  interface Window {
    analytics?: AnalyticsConsoleApi;
  }
}

type AnalyticsConsoleApi = {
  flags: {
    list: () => void;
    set: (flag: string, value: string) => void;
    clear: (flag: string) => void;
    reset: () => void;
  };
  identity: {
    current: () => string;
    isIdentified: () => boolean;
  };
  help: () => void;
};

/**
 * Edge-resolved variant assignment for one experiment, keyed by the flag's
 * PostHog key. Produced either by the CloudFront viewer-request function or
 * the SSR-lambda fallback; consumed during SSR and again on client init.
 *
 * `enrolled: false` is a load-bearing signal: it tells useFlag to return the
 * flag's default WITHOUT consulting PostHog, so non-enrolled users don't fire
 * exposure events and aren't counted in either variant.
 */
export type EdgeFlagState =
  | { enrolled: true; variant: string }
  | { enrolled: false };

export type EdgeFlags = Record<string, EdgeFlagState>;

export type BootstrapData = {
  distinctId: string;
  featureFlags: Record<string, string | boolean>;
  isIdentifiedID?: boolean;
  /** Edge-resolved variant assignments. See WS2 task 4 + Architecture §9. */
  edgeFlags?: EdgeFlags;
};

type ClientAnalyticsConfig = {
  posthogKey: string;
  /**
   * Where posthog-js sends everything — capture, flags, replay, remote config.
   * On deployed stages this is the same-origin ingest proxy
   * (`clientEnv.posthogIngestHost`), not the PostHog instance itself.
   */
  posthogHost: string;
  /**
   * The real PostHog instance, when `posthogHost` is a proxy. posthog-js
   * derives `ui_host` from `api_host` for self-hosted (non-`*.posthog.com`)
   * instances, so without this the toolbar and the replay-player links it
   * builds would point at the proxy path instead of PostHog.
   * @see docs/cloned-repos-as-docs/posthog-js/packages/browser/src/utils/request-router.ts
   */
  posthogUiHost?: string;
  /** Pre-evaluated flags from SSR. Enables zero-flash A/B tests. */
  bootstrap?: BootstrapData;
  /**
   * Edge-resolved variant assignments for cdn-shared routes that don't
   * have a per-user `bootstrap`. Carried separately so cdn-shared init
   * doesn't have to fake a `bootstrap` with empty `distinctId` (PostHog
   * would bake that empty id into its anonymous flow).
   *
   * When both `bootstrap.edgeFlags` and this top-level field are set,
   * this top-level value wins.
   */
  edgeFlags?: EdgeFlags;
  /**
   * PostHog breaking-change defaults version — the date whose bundle of
   * default behaviours posthog-js should apply. Bumping it is a deliberate
   * behaviour change, so the union lists only dates the pinned posthog-js
   * actually understands; widen it when the dep moves.
   *
   * It does NOT control `person_profiles`, which is a plain default of
   * `identified_only` outside the versioned table. That one is set explicitly
   * below — see the note there.
   */
  defaults?:
    | '2026-06-25'
    | '2026-05-30'
    | '2026-01-30'
    | '2025-11-30'
    | '2025-05-24'
    | 'unset';
};

/**
 * Initialize PostHog with optional SSR bootstrap data.
 *
 * When bootstrap is provided:
 * - Flags are available synchronously via posthog.getFeatureFlag() immediately
 * - The initial /flags request is skipped (advanced_disable_feature_flags_on_first_load)
 * - Subsequent identify() calls still trigger flag refetch
 *
 * Called once during RootComponent render via initPostHogFromRouteContext,
 * which reads bootstrap from TanStack Start's route context (server function → beforeLoad).
 * @see https://posthog.com/docs/feature-flags/bootstrapping
 */
export function initClientAnalytics(config: ClientAnalyticsConfig) {
  posthog.init(config.posthogKey, {
    api_host: config.posthogHost,
    ui_host: config.posthogUiHost ?? null,
    defaults: config.defaults,
    person_profiles: 'always',
    // NB: `disable_compression` and `disable_beacon` are deliberately NOT set.
    //
    // Leaving compression on is a decision to follow PostHog's guidance —
    // `disable_compression` is documented "should only be used for testing".
    // The consequence is that the same-origin ingest proxy does NOT defeat
    // uBlock Origin's `/e/*^ip=*^compression=` rule (default privacy list):
    // it is host- and prefix-agnostic, and our capture endpoint (`/i/v0/e/`,
    // from remote config) contains the literal `/e/` it keys on. The rule
    // needs the `compression=` param to be present, which happens once remote
    // config reports `supportedCompression` — so whether a given uBO user's
    // capture is blocked depends on that config load. Verify in a real browser
    // with uBO on rather than reasoning about it.
    //
    // `disable_beacon` was set alongside it and has been removed too: the
    // compression rule already matches every capture request, beacon or not,
    // so disabling sendBeacon bought nothing while costing $pageleave
    // reliability.
    //
    // What the proxy still fixes: EasyPrivacy's `://posthog.$script` rule,
    // which blocks `/array/<token>/config.js` and `/static/<ver>/*.js` from
    // any `posthog.*` host — i.e. remote config and every lazy-loaded
    // extension. Plus host-list rules generally, and the CORS allow-list.
    bootstrap: config.bootstrap
      ? {
          distinctID: config.bootstrap.distinctId,
          featureFlags: config.bootstrap.featureFlags,
          isIdentifiedID: config.bootstrap.isIdentifiedID,
        }
      : undefined,
    // When bootstrapping, skip the redundant /flags fetch on first load.
    // Flags will refresh on identify() or manual reloadFeatureFlags().
    advanced_disable_feature_flags_on_first_load: !!config.bootstrap,
    // We don't use PostHog surveys. Disabling prevents handlePageUnload TypeError
    // from the surveys module on page unload events.
    disable_surveys: true,
    // Session replays record everything (business decision, 2026-08-04):
    // masked replays are close to useless for debugging our reader UI. Only
    // `maskAllInputs: false` does anything here — an explicit `undefined` for
    // the selector fields does NOT override the project setting (posthog-js
    // merges client ?? server per field, so undefined falls through). Password
    // inputs stay masked regardless: posthog-js force-seeds
    // `maskInputOptions.password: true` independently of `maskAllInputs`.
    session_recording: {
      maskAllInputs: false,
      maskTextSelector: undefined,
      blockSelector: undefined,
    },
  });

  const edgeFlagsToApply = config.edgeFlags ?? config.bootstrap?.edgeFlags;
  if (edgeFlagsToApply) {
    applyEdgeFlags(edgeFlagsToApply);
  }

  return posthog;
}

// ---------------------------------------------------------------------------
// EdgeFlagsContext — SSR-safe variant reads (WS2 task 5)
// ---------------------------------------------------------------------------

/**
 * React context carrying the request's edge-resolved variant assignments.
 *
 * Why a context: the `useEdgeFlag` hook is called during both SSR and
 * client rendering and must return the assigned variant in both. Module-
 * scope state (`edgeFlagsMap` below) cannot be populated server-side
 * without leaking across requests in a warm AWS Lambda. The context's
 * value comes from TanStack's route context — populated per request in
 * `__root.tsx`'s `beforeLoad` from `resolveEdgeFlags`. On the client,
 * the same value rides through hydration; `applyEdgeFlags` runs once at
 * init time to do the PostHog side (overrides, super-properties, exposure
 * events) and to populate the module-scope map for the non-hook
 * `getFlagValue` escape hatch.
 */
const EdgeFlagsContext = createContext<EdgeFlags | undefined>(undefined);

/**
 * Provide `edgeFlags` to descendants. Render once high in the tree (e.g.
 * `__root.tsx`'s `RootComponent`) with the value from route context.
 *
 * On SSR this is the only mechanism that makes `useFlag` return the
 * assigned variant — the module-scope `edgeFlagsMap` is never populated
 * server-side by design.
 */
export function EdgeFlagsProvider({
  edgeFlags,
  children,
}: {
  edgeFlags: EdgeFlags | undefined;
  children: ReactNode;
}) {
  return createElement(
    EdgeFlagsContext.Provider,
    { value: edgeFlags },
    children,
  );
}

// ---------------------------------------------------------------------------
// Edge-bucketed flag state (WS2 tasks 4 + 5)
// ---------------------------------------------------------------------------

/**
 * Module-local view of edge-resolved assignments for this page load. Read by
 * useEdgeFlag/getFlagValue; replaced wholesale on each applyEdgeFlags call.
 */
const edgeFlagsMap = new Map<string, EdgeFlagState>();

/**
 * Exposure-event dedupe set, keyed by `${distinctId}:${flagKey}:${variant}`.
 * Lives in module scope so re-running initClientAnalytics within the same
 * session doesn't double-count. Resets on page reload.
 */
const firedExposures = new Set<string>();

/**
 * Session-scoped dedupe for {@link reportStaleOverrideReclaimed} — one event
 * per flag per page lifetime, so a SPA session with many navigations reports
 * the anomaly once instead of per-route.
 */
const reportedStaleOverrides = new Set<string>();

/**
 * A `$override_feature_flags` entry with no matching manual-intent record was
 * found disagreeing with the edge assignment and reclaimed. This should be
 * rare (an interrupted manual override, posthog persistence resurrected after
 * a mid-session storage clear) — before the reclaim-by-default fix, this
 * exact state silently FROZE the visitor on a stale arm, flickering prices on
 * every marketing page. The event exists so any residual source of stale
 * overrides shows up in PostHog instead of a user's console; a sustained rate
 * of it means some code path is writing overrides without registering intent.
 *
 * Best-effort: the beforeLoad-time `applyEdgeFlags` call can run before
 * `posthog.init`, where captures are dropped — the init-time call re-reports
 * because the reclaim only takes effect once the write reaches persistence.
 */
function reportStaleOverrideReclaimed(
  flagKey: string,
  staleValue: string,
  edgeVariant: string | null,
) {
  const dedupeKey = `${flagKey}:${staleValue}`;
  if (reportedStaleOverrides.has(dedupeKey)) return;
  reportedStaleOverrides.add(dedupeKey);
  posthog.capture('edge_stale_override_reclaimed', {
    flag_key: flagKey,
    stale_value: reportedVariant(flagKey, staleValue),
    edge_variant:
      edgeVariant === null ? null : reportedVariant(flagKey, edgeVariant),
  });
}

/**
 * PostHog flag key → variant, for every experiment the most recent
 * `applyEdgeFlags` enrolled this visitor in and did not defer to a dev
 * override. It is what `fireExposure` is allowed to emit for, which is how
 * "no exposure without enrolment" holds structurally rather than by
 * convention.
 *
 * Not cleared wholesale on each `applyEdgeFlags` — a deferred experiment's
 * moment (a paywall, a checkout step) is usually on a different route than
 * the page that enrolled the visitor, and that route's `EdgeFlags` map does
 * not mention the flag at all. Only keys the incoming map speaks to are
 * updated or dropped.
 */
const enrolledForExposure = new Map<string, string>();

/**
 * Whether this browser announces itself as a known crawler, via the same
 * pattern the edge function and the SSR lambda pin bots with.
 *
 * Read from `navigator` rather than plumbed down from the resolvers: the
 * suppression below is a client-side decision about a client-side event, and
 * the browser knows its own user-agent — the same string the lambda reads.
 * Carrying it on `EdgeFlagState` instead would change the resolved shape that
 * `apps/start/test/unit/analytics/edge-bot-pinning.test.ts` pins as identical
 * across the edge and the lambda, and it would buy nothing: no server-side
 * consumer of the resolved flags needs to know.
 */
function isBotViewer(): boolean {
  if (typeof navigator === 'undefined') return false;
  return isBotUserAgent(navigator.userAgent);
}

/**
 * Registry of flag overrides a HUMAN deliberately set — via the console API
 * (`analytics.flags.set`) or the devtools panel. Keyed on flag key (PostHog
 * key), value = the variant they chose.
 *
 * This is the provenance record `applyEdgeFlags` consults before overwriting
 * a PostHog override with the edge assignment: an override is "manual" only
 * if this registry lists it WITH the same value. Everything else in
 * `$override_feature_flags` is presumed ours and is reclaimed.
 *
 * That default direction is the point. The previous design inferred manual
 * intent from the ABSENCE of our own bookkeeping ("we didn't record setting
 * this, so the user must have"), which fails open: any way the bookkeeping
 * can be lost while PostHog's persistence survives — posthog-js re-saving
 * its in-memory state after a mid-session "Clear site data", a partial
 * storage clear, a quota-failed write — permanently froze the visitor on a
 * stale arm, rendering prices that disagree with what checkout quotes (the
 * C4 direction). Recording intent at the moment of intent fails closed
 * instead: losing THIS record merely costs a QA engineer their override,
 * and a stale PostHog override self-heals on the next navigation.
 */
const MANUAL_FLAG_OVERRIDES_KEY = 'listening_manual_flag_overrides';
const manualOverrideListeners = new Set<() => void>();

export function subscribeManualFlagOverrides(onChange: () => void) {
  manualOverrideListeners.add(onChange);
  return () => {
    manualOverrideListeners.delete(onChange);
  };
}

function notifyManualFlagOverrides() {
  for (const listener of manualOverrideListeners) listener();
}

function readManualFlagOverrides(): Record<string, string> {
  try {
    const raw = localStorage.getItem(MANUAL_FLAG_OVERRIDES_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, string>;
  } catch {
    return {};
  }
}

function writeManualFlagOverrides(value: Record<string, string>) {
  try {
    if (Object.keys(value).length === 0) {
      localStorage.removeItem(MANUAL_FLAG_OVERRIDES_KEY);
    } else {
      localStorage.setItem(MANUAL_FLAG_OVERRIDES_KEY, JSON.stringify(value));
    }
  } catch {
    // Quota / private mode — the override below still applies for this page
    // load, but without the registry entry `applyEdgeFlags` reclaims it on
    // the next navigation. Failing toward "QA override lost" is the safe
    // direction; the old design failed toward "user frozen on a stale arm".
  }
}

/**
 * Set a manual (DevTools/console) override for a PostHog-keyed flag. The ONLY
 * legitimate writers are the console API below and the devtools panel — app
 * code must never call this. Records intent FIRST, then applies the override,
 * so an interruption between the two leaves a reclaimable override rather
 * than an unrecorded one.
 */
export function setManualFlagOverride(flagKey: string, value: string) {
  writeManualFlagOverrides({ ...readManualFlagOverrides(), [flagKey]: value });
  posthog.featureFlags.overrideFeatureFlags({
    flags: { ...readOverrideFlags(), [flagKey]: value },
  });
  // Drop the edge super-property now, not on the next navigation:
  // `applyEdgeFlags` skips manually-overridden keys entirely, so a
  // `$feature/*` registered before the override was set would otherwise keep
  // reporting the edge arm the user is no longer seeing.
  posthog.unregister(`$feature/${flagKey}`);
  notifyManualFlagOverrides();
}

/** Remove one manual override (registry entry + PostHog override). */
export function clearManualFlagOverride(flagKey: string) {
  const manual = readManualFlagOverrides();
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- keyed by flag key
  delete manual[flagKey];
  writeManualFlagOverrides(manual);
  const { [flagKey]: _, ...rest } = readOverrideFlags();
  if (Object.keys(rest).length === 0) {
    posthog.featureFlags.overrideFeatureFlags(false);
  } else {
    posthog.featureFlags.overrideFeatureFlags({ flags: rest });
  }
  notifyManualFlagOverrides();
}

/** Remove every manual override (registry + all PostHog overrides). */
export function clearAllManualFlagOverrides() {
  writeManualFlagOverrides({});
  posthog.featureFlags.overrideFeatureFlags(false);
  notifyManualFlagOverrides();
}

/**
 * **Intended caller: TanStack root `beforeLoad`'s client branch.** Not a
 * general-purpose effect helper.
 *
 * Most call sites should NEVER reach for this directly — variant resolution
 * and exposure firing belong in the route-context plumbing path, so the
 * variant is bound to a navigation cause and the dedupe set
 * (`firedExposures`) and manual-override detection work correctly. Calling
 * `applyEdgeFlags` from arbitrary effects/components risks double-firing
 * exposure events with mismatched contexts, leaking `$feature/<key>` super-
 * properties across user sessions, and clobbering DevTools overrides
 * (see the "Manual DevTools / console overrides are preserved" note below).
 *
 * If you need to read the current variant, use `useFlag(...)` /
 * `getFlagValue(...)`. If you need to set a variant manually for QA, use
 * the `analytics.flags.set(...)` console API documented in
 * `docs/guidelines/web/edge-bucketed-experiments.md`.
 *
 * Exported (rather than module-private) only because the SPA-navigation fix
 * in `apps/start/src/routes/__root.tsx`'s `beforeLoad` needs to re-apply
 * edge flags on every nav, and `initClientAnalytics` (the original caller)
 * runs exactly once per page lifetime.
 *
 * ---
 *
 * Apply edge-resolved variant assignments to PostHog state and the module's
 * edgeFlagsMap. Idempotent within a session (dedupe set + override map).
 *
 * For enrolled flags:
 *   1. Set `$override_feature_flags[key] = variant` so posthog-js returns the
 *      same value across `/flags` refetches.
 *   2. Register `$feature/<key>: variant` as a super-property so subsequent
 *      `capture()` calls auto-tag the event with the variant.
 *   3. Fire `$feature_flag_called` (deduped by flag+variant+distinctId) for
 *      experiments whose `exposureAt` label is owned by this call site.
 *      PostHog's auto-exposure only fires when `posthog.getFeatureFlag()` is
 *      called, which useEdgeFlag deliberately skips — so we fire here.
 *      An experiment whose label is `deferred` is registered and overridden
 *      here like any other, but stays out of the denominator until app code
 *      calls `fireExposure` at the declared moment.
 *
 * For non-enrolled flags — and for every flag when the viewer is a known
 * crawler, which is outside the experiment for the same reason:
 *   - Remove any stale override for this key (without nuking unrelated dev
 *     overrides — see Architecture §9 warning).
 *   - Unregister the `$feature/<key>` super-property in case it leaked from
 *     a previous session where the user WAS enrolled.
 *
 * **Manual DevTools / console overrides are preserved.** "Manual" means the
 * `MANUAL_FLAG_OVERRIDES_KEY` registry lists the key with the same value —
 * the record `setManualFlagOverride` writes at the moment a human sets one.
 * A registered manual override is left alone (skip overwriting + skip firing
 * exposure for it — exposure is for the "true" edge-assigned variant, not
 * the dev-override value). Any OTHER override for a key this map speaks to
 * is presumed ours — possibly stale, resurrected, or orphaned — and is
 * reclaimed to the edge assignment. See the registry's doc comment for why
 * unrecognized-means-ours is the safe default.
 *
 * Do NOT call `posthog.featureFlags.overrideFeatureFlags(false)` as a
 * "clean slate" — that nukes dev overrides set via the DevTools panel,
 * which we want to preserve.
 *
 * NOTE: posthog-js v1.354.2 only accepts the wrapped shape
 * `{flags: {key: variant}}`. Passing the legacy bare-object shape is a
 * silent no-op that falls through to `_fireFeatureFlagsCallbacks()` —
 * verified in node_modules/posthog-js/lib/src/posthog-featureflags.js
 * lines 790-831. The `overrides` snapshot we read at the top is the full
 * current override map, so passing it back through `{flags: overrides}`
 * preserves any DevTools-set overrides for unrelated flags.
 */
export function applyEdgeFlags(edgeFlags: EdgeFlags) {
  edgeFlagsMap.clear();
  const overrides = readOverrideFlags();
  const manual = readManualFlagOverrides();
  const enrolledEntries: { key: string; variant: string }[] = [];
  const nonEnrolledKeys: string[] = [];
  // A known crawler is outside the experiment, not a third arm (§3.5): the
  // edge pins it to the declared `fallbackVariant`, and it must stay out of
  // the denominator entirely. It therefore takes the same PostHog path as a
  // non-enrolled visitor — no `$feature/<key>`, no exposure, and no entry in
  // `enrolledForExposure` for a later `fireExposure` to find. What renders is
  // untouched: `edgeFlagsMap` below still carries the assignment, so `useFlag`
  // returns exactly the arm the SSR HTML rendered.
  //
  // Most crawlers never execute JS and never reach this code. The ones that
  // do — headless browsers — are precisely the traffic that would otherwise
  // land in the denominator, and at SEO volume that drowns
  // revenue-per-exposed in zeros (C5).
  const botViewer = isBotViewer();

  for (const [key, state] of Object.entries(edgeFlags)) {
    edgeFlagsMap.set(key, state);
    // Re-earned below by enrolled flags with no dev override, so losing
    // enrolment — or gaining an override — also loses the right to fire a
    // deferred exposure later in the session.
    enrolledForExposure.delete(key);
    const currentOverride = overrides[key];
    // "Manual" = a human registered this exact override via the console API
    // or devtools panel. A registry entry whose value no longer matches the
    // live override describes an intent that no longer holds (the override
    // drifted or was lost), so it does not protect anything.
    const isManualOverride =
      typeof currentOverride === 'string' && manual[key] === currentOverride;

    if (state.enrolled && !botViewer) {
      if (isManualOverride) {
        // Preserve dev override; don't fire exposure for the edge variant
        // (it's not what the user is seeing).
        continue;
      }
      if (
        typeof currentOverride === 'string' &&
        currentOverride !== state.variant
      ) {
        reportStaleOverrideReclaimed(key, currentOverride, state.variant);
      }
      enrolledEntries.push({ key, variant: state.variant });
      enrolledForExposure.set(key, state.variant);
      overrides[key] = state.variant;
    } else {
      if (isManualOverride) {
        // User wants this variant even though edge says non-enrolled. Leave
        // both the override and the super-property alone.
        continue;
      }
      if (typeof currentOverride === 'string') {
        reportStaleOverrideReclaimed(key, currentOverride, null);
      }
      nonEnrolledKeys.push(key);
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- keyed by flag key
      delete overrides[key];
    }
  }

  posthog.featureFlags.overrideFeatureFlags({ flags: overrides });

  for (const { key, variant } of enrolledEntries) {
    posthog.register({ [`$feature/${key}`]: reportedVariant(key, variant) });
  }
  for (const key of nonEnrolledKeys) {
    posthog.unregister(`$feature/${key}`);
  }

  const distinctId = posthog.get_distinct_id();
  for (const { key, variant } of enrolledEntries) {
    if (exposureTriggerForFlagKey(key) !== 'page-load') continue;
    captureExposure(distinctId, key, variant);
  }
}

/**
 * Flags whose variant is a transport ENCODING rather than the name the rest of
 * the world knows the arm by.
 *
 * `pricing-test-v2` is the only one: its variant names do not fit the
 * CloudFront viewer-request byte budget, so the edge hashes and stamps
 * single-character codes (`a`, `b`) instead
 * (`@listening/api-contract/pricing-experiment`). PostHog sits on the durable
 * side of that boundary — its IaC experiment's variants, the `$feature/<key>`
 * super-property and the exposure event's `$feature_flag_response` are all
 * variant names — so the code is decoded here, at the one place a variant becomes
 * something PostHog reads. What `useFlag` returns is untouched: the app decodes
 * at its own boundary, and the `$override_feature_flags` value stays the code
 * so a DevTools override still speaks the same vocabulary as the edge.
 *
 * Decoders must be TOTAL and IDEMPOTENT — anything that is not a live code (a
 * variant name already, a test fixture) passes through unchanged — because
 * the same helper serves both edge-resolved codes and the variant names
 * {@link registerServerAssignment} carries.
 */
const VARIANT_DECODERS: Record<
  string,
  ((variant: string) => string) | undefined
> = {
  [PRICING_TEST_V2_FLAG_KEY]: (variant) =>
    decodePricingEdgeCode(variant) ?? variant,
};

/** The name PostHog knows this variant by. */
function reportedVariant(flagKey: string, variant: string): string {
  return VARIANT_DECODERS[flagKey]?.(variant) ?? variant;
}

/**
 * Adopt a variant the SERVER resolved for the signed-in account, for
 * experiments whose assignment outranks anything this browser can compute.
 *
 * Mode C's account pin is such an assignment: it is a database row, so a
 * visitor who signed up on another device — or cleared the cookie the edge
 * bucketed them from — has an arm that `applyEdgeFlags` cannot know. Without
 * this, an authenticated surface would report the browser's arm (or none at
 * all, on any route the edge does not bucket), and the analysis would be
 * measuring a different assignment than the one the money followed. Design
 * §3.6: "post-pin, authenticated surfaces report the pinned variant as the same
 * super-property, so later cookie drift cannot pollute the analysis."
 *
 * Registers the `$feature/<key>` super-property and makes the flag eligible for
 * a deferred {@link fireExposure} — the two things `applyEdgeFlags` does for an
 * enrolled visitor. Deliberately does NOT touch `$override_feature_flags`: the
 * server's answer is about reporting and money, and silently overwriting a
 * DevTools override would change what the page renders.
 *
 * Call it only for an account the server says is an experiment SUBJECT. A
 * fallback-quoted account is served a defined price without being randomized
 * into it; reporting it as an arm would put unrandomized revenue in the results.
 */
export function registerServerAssignment(flagKey: string, variant: string) {
  enrolledForExposure.set(flagKey, variant);
  posthog.register({
    [`$feature/${flagKey}`]: reportedVariant(flagKey, variant),
  });
}

/**
 * Emit the exposure event for every enrolled experiment that declared this
 * `exposureAt` label, at most once per session per experiment.
 *
 * The other half of the `exposureAt` contract. An experiment whose exposure
 * belongs to a moment no router event describes — a paywall render, a
 * checkout step — is registered and overridden at page load like any other,
 * but only lands in the denominator when its moment actually happens and the
 * code for that moment calls this. Marketing pageviews therefore cannot
 * expose it, which is the whole point: SEO traffic in the denominator drowns
 * revenue-per-exposed in zeros (design C5).
 *
 * Emits nothing for a label no live experiment declares, for a visitor the
 * edge did not enrol, or for a flag the visitor has dev-overridden. Deduped
 * against the same set page-load exposures use, so calling it on every render
 * of the moment is free.
 */
export function fireExposure(label: PolicyExposureLabel) {
  const distinctId = posthog.get_distinct_id();
  for (const [key, variant] of enrolledForExposure) {
    if (exposureLabelForFlagKey(key) !== label) continue;
    captureExposure(distinctId, key, variant);
  }
}

function captureExposure(distinctId: string, key: string, variant: string) {
  // Deduped on the REPORTED name, so a code-carrying edge assignment and the
  // arm key a server assignment carries for the same arm are one exposure.
  const reported = reportedVariant(key, variant);
  const dedupeKey = `${distinctId}:${key}:${reported}`;
  if (firedExposures.has(dedupeKey)) return;
  firedExposures.add(dedupeKey);
  posthog.capture('$feature_flag_called', {
    $feature_flag: key,
    $feature_flag_response: reported,
  });
}

/**
 * Test/debug only. Reset module-local edge state so successive tests don't
 * leak state between cases. NOT for use in app code.
 */
export function _resetEdgeFlagsForTest() {
  edgeFlagsMap.clear();
  firedExposures.clear();
  enrolledForExposure.clear();
  reportedStaleOverrides.clear();
  try {
    localStorage.removeItem(MANUAL_FLAG_OVERRIDES_KEY);
  } catch {
    // localStorage not available in test env — ignore.
  }
}

/**
 * Get the PostHog singleton.
 *
 * Does NOT throw when `initClientAnalytics()` hasn't run — posthog-js hands
 * back an uninitialized instance whose reads are empty (`get_distinct_id()`
 * returns undefined) and whose captures go nowhere. Callers that need a live
 * client must ensure init has happened rather than relying on an error.
 */
export function getPostHogClient() {
  return posthog;
}

/**
 * Record this session even though sampling would have skipped it.
 *
 * `session_recording_sample_rate` is 0.1 because unsampled replay was measured
 * at ~48 GB/month, which the self-hosted box cannot carry. That rate is right
 * for the site and wrong for a funnel: onboarding sees tens of sessions a week,
 * so a 10% sample delivers roughly nothing to watch.
 *
 * `{ sampling: true }` opts THIS session in and touches nothing else — it is
 * not the team-level URL-trigger config, which applies to every visitor and
 * changes what recording means globally. Safe to call repeatedly; posthog-js
 * no-ops once a recording is running.
 *
 * Depends on the replay config actually reaching the browser. While
 * `/array/<token>/config` 404s there is no recorder to start and this is inert
 * — see packages/infra/src/ovh/boxes/posthog/conf/posthog/docker-compose.override.yml.ts.
 */
export function forceSessionRecording() {
  posthog.startSessionRecording({ sampling: true });
}

/** Map of client flag names to their PostHog keys, for consumers that need the raw key */
export const clientFlagKeys = Object.fromEntries(
  Object.entries(ClientFlags).map(([name, def]) => [name, def.key]),
) as Record<keyof typeof ClientFlags, string>;

/**
 * Read a flag value synchronously (non-hook). Use this outside React components
 * (e.g., in API middleware, module-level code, or event handlers).
 *
 * Local flags read from localStorage. PostHog flags read from the PostHog SDK.
 */
export function getFlagValue<F extends ClientFlagName>(
  flag: F,
): FlagVariant<(typeof ClientFlags)[F]> {
  const def = ClientFlags[flag];
  type V = FlagVariant<(typeof ClientFlags)[F]>;

  if (def.bucketing.mode === 'local') {
    const local = readLocalFlags();
    return (local[def.key] as V | undefined) ?? (def.default as V);
  }

  if (def.bucketing.mode === 'edge') {
    return readEdgeFlagValue(def.key, def.default as V);
  }

  return (
    (posthog.getFeatureFlag(def.key) as V | undefined) ?? (def.default as V)
  );
}

/**
 * Resolve an edge-bucketed flag value, given an optional pre-fetched
 * context map (for hook callers that already pulled the value from
 * `EdgeFlagsContext`). Reads, in order:
 *
 *   1. PostHog override (devtools panel / console API) — for dev ergonomics.
 *   2. The context map (if provided) — set on SSR + client via the
 *      `EdgeFlagsProvider` in the React tree.
 *   3. The module-scope `edgeFlagsMap` (client-only, populated by
 *      `applyEdgeFlags` during `initClientAnalytics`) — used for the
 *      non-hook `getFlagValue` escape hatch and as a hook fallback in
 *      rare cases where the provider isn't in scope.
 *   4. The flag's default (non-enrolled users, or no edge assignment present).
 *
 * **Step 4 collapses "not enrolled" and "never assigned" into one value, and
 * that is not safe for every flag.** It is fine for a funnel flag whose default
 * arm is also its control. It is NOT fine for a price flag: `pricingTestV2`
 * defaults to `'a'` ($39, the CHEAPEST arm), so an unregistered route or a
 * crawler would read a value that advertises less than an enrolled buyer is
 * charged — the one direction decision C4 forbids. A caller that must tell the
 * two apart reads assignment PRESENCE off the `EdgeFlags` map and does not
 * treat this return value as evidence that an assignment exists — it still
 * takes the value from here once presence is established (see
 * `use-marketing-pricing-arm.ts`). So changing what this answers for an
 * unassigned flag would break that silently, as a price.
 *
 * Does NOT call `posthog.getFeatureFlag()` — that would auto-fire a
 * `$feature_flag_called` exposure event for non-enrolled users, polluting
 * experiment math. The exposure event for enrolled users is fired once per
 * session from `applyEdgeFlags`.
 */
function readEdgeFlagValue<V>(
  flagKey: string,
  defaultValue: V,
  ctxEdgeFlags?: EdgeFlags,
): V {
  const override = posthog.persistence
    ? readOverrideFlags()[flagKey]
    : readManualFlagOverrides()[flagKey];
  if (typeof override === 'string') return override as V;
  const ctxState = ctxEdgeFlags?.[flagKey];
  if (ctxState?.enrolled) return ctxState.variant as V;
  if (ctxState && !ctxState.enrolled) return defaultValue;
  const mapState = edgeFlagsMap.get(flagKey);
  if (mapState?.enrolled) return mapState.variant as V;
  return defaultValue;
}

// ---------------------------------------------------------------------------
// React Hooks
// ---------------------------------------------------------------------------

type UseFlagResult<F extends ClientFlagName> = {
  /** Current flag value. Falls back to typed default if not loaded. */
  value: FlagVariant<(typeof ClientFlags)[F]>;
  /** True when flags are being refetched (e.g., after identify() or reset()). */
  isLoading: boolean;
  /** True when a dev override is active for this flag. */
  isOverridden: boolean;
};

/**
 * React hook for reading a feature flag with loading state and override awareness.
 *
 * @example
 * ```tsx
 * const { value, isLoading } = useFlag('backendVersion');
 * if (isLoading) return <Skeleton />;
 * return value === 'effect-ts' ? <NewBackend /> : <LegacyBackend />;
 * ```
 */
export function useFlag<F extends ClientFlagName>(flag: F): UseFlagResult<F> {
  const def = ClientFlags[flag];

  if (def.bucketing.mode === 'local') {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- bucketing.mode is static per flag definition
    return useLocalFlag<F>(def);
  }

  if (def.bucketing.mode === 'edge') {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- bucketing.mode is static per flag definition
    return useEdgeFlag<F>(def);
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks -- bucketing.mode is static per flag definition
  return usePostHogFlag<F>(def);
}

function useLocalFlag<F extends ClientFlagName>(
  def: (typeof ClientFlags)[F],
): UseFlagResult<F> {
  type V = FlagVariant<(typeof ClientFlags)[F]>;

  const readValue = useCallback(() => {
    const local = readLocalFlags();
    return (local[def.key] as V | undefined) ?? (def.default as V);
  }, [def.key, def.default]);

  const [value, setValue] = useState(readValue);

  useEffect(() => {
    const handler = () => setValue(readValue());
    window.addEventListener(LOCAL_FLAGS_EVENT, handler);
    return () => window.removeEventListener(LOCAL_FLAGS_EVENT, handler);
  }, [readValue]);

  const isOverridden = def.key in readLocalFlags();

  return { value, isLoading: false, isOverridden };
}

function useEdgeFlag<F extends ClientFlagName>(
  def: (typeof ClientFlags)[F],
): UseFlagResult<F> {
  type V = FlagVariant<(typeof ClientFlags)[F]>;

  const ctxEdgeFlags = useContext(EdgeFlagsContext);

  const readState = useCallback(
    () => ({
      value: readEdgeFlagValue(def.key, def.default as V, ctxEdgeFlags),
      isOverridden:
        checkIsPostHogOverridden(def.key) ||
        (!posthog.persistence && def.key in readManualFlagOverrides()),
    }),
    [def.key, def.default, ctxEdgeFlags],
  );

  const [state, setState] = useState(readState);

  // Resync if the context changes mid-mount (rare; included for correctness).
  useEffect(() => {
    setState(readState());
  }, [readState]);

  useEffect(() => {
    const onChange = () => setState(readState());
    const unsubscribePostHog = posthog.onFeatureFlags(onChange);
    const unsubscribeManual = subscribeManualFlagOverrides(onChange);
    return () => {
      unsubscribePostHog();
      unsubscribeManual();
    };
  }, [readState]);

  return { ...state, isLoading: false };
}

function usePostHogFlag<F extends ClientFlagName>(
  def: (typeof ClientFlags)[F],
): UseFlagResult<F> {
  type V = FlagVariant<(typeof ClientFlags)[F]>;

  const readValue = useCallback(
    () =>
      (posthog.getFeatureFlag(def.key) as V | undefined) ?? (def.default as V),
    [def.key, def.default],
  );

  const [value, setValue] = useState(readValue);
  const [isLoading, setIsLoading] = useState(
    !posthog.featureFlags?.hasLoadedFlags,
  );

  useEffect(() => {
    const unsub = posthog.onFeatureFlags(() => {
      setValue(readValue());
      setIsLoading(false);
    });

    // posthog.on() supports 'featureFlagsReloading' at runtime but published
    // types (v1.336.4) only expose 'eventCaptured'. Widen inline to avoid
    // extracting the method (which triggers unbound-method).
    const unsubReloading = (
      posthog.on as (event: string, cb: () => void) => () => void
    )('featureFlagsReloading', () => setIsLoading(true));

    return () => {
      unsub();
      unsubReloading();
    };
  }, [readValue]);

  const isOverridden = checkIsPostHogOverridden(def.key);

  return { value, isLoading, isOverridden };
}

function checkIsPostHogOverridden(flagKey: string) {
  try {
    const raw: unknown = posthog.persistence?.props?.$override_feature_flags;
    if (raw === null || raw === undefined || typeof raw !== 'object')
      return false;
    return flagKey in raw;
  } catch {
    return false;
  }
}

type UseAnalyticsResult = {
  /**
   * Track a typed analytics event.
   * @example capture('started_playing_audio', { audio_item_id: '123', ... })
   */
  capture: <E extends keyof ClientEvents>(
    event: E,
    properties: ClientEvents[E],
  ) => void;
  /**
   * Identify the current user. Call on login/signup.
   * Triggers an async flag refetch for the new user.
   */
  identify: (userId: string, traits?: Record<string, unknown>) => void;
  /**
   * Reset identity and flags. Call on logout.
   * Generates a new anonymous distinct_id and refetches flags.
   */
  reset: () => void;
};

const DEFAULT_PROPERTIES = {
  platform: 'web' as const,
  /**
   * Which client sent the event. `platform` is the coarse axis (`web` vs
   * `mobile`); this is the fine one, and the only thing that separates the
   * Flutter app (`flutter-app`) from the Expo port (`expo-app`), which share
   * `platform: 'mobile'`. Both mobile clients have always sent it; web is new
   * here, so web events before this deploy have no `category` and land in
   * PostHog's "none" bucket on any breakdown over it.
   */
  category: 'web-app' as const,
};

/**
 * React hook for analytics event tracking and identity management.
 *
 * @example
 * ```tsx
 * const { capture, identify, reset } = useAnalytics();
 * capture('started_playing_audio', { audio_item_id: '123', ... });
 * ```
 */
export function useAnalytics(): UseAnalyticsResult {
  const capture = useCallback(
    <E extends keyof ClientEvents>(event: E, properties: ClientEvents[E]) => {
      posthog.capture(event, {
        ...DEFAULT_PROPERTIES,
        ...(properties as Record<string, unknown>),
      });
    },
    [],
  );

  const identify = useCallback(
    (userId: string, traits?: Record<string, unknown>) => {
      posthog.identify(userId, traits);
    },
    [],
  );

  const reset = useCallback(() => {
    posthog.reset();
    clearAllLocalFlags();
  }, []);

  return { capture, identify, reset };
}

// ---------------------------------------------------------------------------
// Console API (dev overrides)
// ---------------------------------------------------------------------------

/**
 * Attach a console API for flag overrides in non-production environments.
 *
 * Usage in browser console:
 * - analytics.flags.list()                             — print all flags
 * - analytics.flags.set('backendVersion', 'effect-ts') — override a flag
 * - analytics.flags.clear('backendVersion')            — remove override
 * - analytics.flags.reset()                            — clear all overrides
 * - analytics.identity.current()                       — print distinct_id
 * - analytics.help()                                   — print commands
 *
 * Overrides use PostHog's built-in overrideFeatureFlags() API which persists
 * in localStorage under $override_feature_flags.
 */
/* oxlint-disable no-console -- This function IS a console debugging tool */
export function attachConsoleApi(flags: FlagRecord) {
  if (typeof window === 'undefined') return;

  const flagEntries = Object.entries(flags);

  window.analytics = {
    flags: {
      list: () => {
        const phVariants = posthog.featureFlags?.getFlagVariants?.() ?? {};
        const phOverrides = readOverrideFlags();
        const localValues = readLocalFlags();

        const rows = flagEntries.map(([name, def]) => ({
          name,
          key: def.key,
          bucketing: def.bucketing.mode,
          value:
            def.bucketing.mode === 'local'
              ? (localValues[def.key] ?? def.default)
              : (phVariants[def.key] ?? def.default),
          default: def.default,
          variants: def.variants.join(', '),
          overridden:
            def.bucketing.mode === 'local'
              ? def.key in localValues
              : def.key in phOverrides,
        }));

        console.table(rows);
      },

      set: (flagName: string, value: string) => {
        const def = flags[flagName];
        if (!def) {
          console.error(
            `Unknown flag "${flagName}". Available: ${flagEntries.map(([n]) => n).join(', ')}`,
          );
          return;
        }
        if (!def.variants.includes(value)) {
          console.error(
            `Invalid value "${value}" for flag "${flagName}". Valid: ${def.variants.join(', ')}`,
          );
          return;
        }

        if (def.bucketing.mode === 'local') {
          setLocalFlag(def.key, value);
        } else {
          // Registers the manual-intent record too — without it,
          // `applyEdgeFlags` reclaims the override on the next navigation.
          setManualFlagOverride(def.key, value);
        }
        console.log(`✓ ${flagName} → ${value}`);
      },

      clear: (flagName: string) => {
        const def = flags[flagName];
        if (!def) {
          console.error(`Unknown flag "${flagName}".`);
          return;
        }

        if (def.bucketing.mode === 'local') {
          clearLocalFlag(def.key);
        } else {
          clearManualFlagOverride(def.key);
        }
        console.log(`✓ Cleared override for ${flagName}`);
      },

      reset: () => {
        clearAllManualFlagOverrides();
        clearAllLocalFlags();
        console.log('✓ All overrides cleared');
      },
    },

    identity: {
      current: () => posthog.get_distinct_id(),
      isIdentified: () =>
        posthog.persistence?.props?.$user_state === 'identified',
    },

    help: () => {
      console.log(`
Analytics Console API:
  analytics.flags.list()                  — Print all flags with current values
  analytics.flags.set('name', 'variant')  — Override a flag
  analytics.flags.clear('name')           — Remove a flag override
  analytics.flags.reset()                 — Clear all overrides
  analytics.identity.current()            — Print current distinct_id
  analytics.identity.isIdentified()       — Check if user is identified
  analytics.help()                        — Print this help
      `);
    },
  };
}
/* oxlint-enable no-console */

// ---------------------------------------------------------------------------
// Local flag storage (for source: 'local' flags)
// ---------------------------------------------------------------------------

const LOCAL_FLAGS_KEY = 'listening_local_flags';
const LOCAL_FLAGS_EVENT = 'listening:local-flags-changed';

/** Read all local flag values from localStorage. */
export function readLocalFlags(): Record<string, string> {
  try {
    const raw = localStorage.getItem(LOCAL_FLAGS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, string>;
  } catch {
    return {};
  }
}

/** Set a local flag value in localStorage and notify subscribers. */
export function setLocalFlag(key: string, value: string) {
  const flags = readLocalFlags();
  flags[key] = value;
  localStorage.setItem(LOCAL_FLAGS_KEY, JSON.stringify(flags));
  window.dispatchEvent(new Event(LOCAL_FLAGS_EVENT));
}

/** Remove a local flag override, reverting to its default. */
export function clearLocalFlag(key: string) {
  const flags = readLocalFlags();
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- keyed by flag key
  delete flags[key];
  if (Object.keys(flags).length === 0) {
    localStorage.removeItem(LOCAL_FLAGS_KEY);
  } else {
    localStorage.setItem(LOCAL_FLAGS_KEY, JSON.stringify(flags));
  }
  window.dispatchEvent(new Event(LOCAL_FLAGS_EVENT));
}

/** Clear all local flag overrides. */
export function clearAllLocalFlags() {
  localStorage.removeItem(LOCAL_FLAGS_KEY);
  window.dispatchEvent(new Event(LOCAL_FLAGS_EVENT));
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Read PostHog's override flags from persistence.
 * PostHog types `persistence.props` as `any`, so we narrow through `unknown`.
 */
export function readOverrideFlags(): Record<string, string | boolean> {
  try {
    const raw: unknown = posthog.persistence?.props?.$override_feature_flags;
    if (
      raw !== null &&
      raw !== undefined &&
      typeof raw === 'object' &&
      !Array.isArray(raw)
    ) {
      return { ...(raw as Record<string, string | boolean>) };
    }
    return {};
  } catch {
    return {};
  }
}

/**
 * The flag overrides a human deliberately set, for the DevTools/QA surfaces
 * that list them (`docs-experiment-selector`).
 *
 * Reads the declared registry rather than diffing PostHog's active overrides
 * against an edge-managed copy, which is what this did before: an override
 * with no bookkeeping entry was indistinguishable from user intent, so a
 * mid-session storage clear resurrected our own override and froze the
 * visitor on a stale arm. Manual intent is now recorded when it is set, and
 * anything the registry does not vouch for is reclaimed to the edge
 * assignment by `applyEdgeFlags`.
 */
export function readManualOverrideFlags(): Record<string, string | boolean> {
  return { ...readManualFlagOverrides() };
}
